/**
 * Alta / edición de un petshop (autoservicio, solo admin).
 * Campos: nombre completo, nombre corto (el del botón), dueño y foto del QR
 * (para transferirle al rendir).
 */
import { useEffect, useState } from 'react';
import { Image, Platform, Pressable, Text, View } from 'react-native';
import { QrCode } from 'lucide-react-native';

import { Button, Sheet, TextField } from '@/components/ui';
import { upsertPetshop } from '@/services/firebase/petshops';
import { subirACloudinary } from '@/services/cloudinary';
import { colors, radius, typography } from '@/theme/tokens';
import type { PetshopMeta } from '@/hooks/usePetshopsMeta';

export function PetshopFormSheet({
  visible,
  onClose,
  petshop,
}: {
  visible: boolean;
  onClose: (nuevoId?: string) => void;
  petshop: PetshopMeta | null; // null = nuevo
}) {
  const [nombre, setNombre] = useState('');
  const [corto, setCorto] = useState('');
  const [dueno, setDueno] = useState('');
  const [telefono, setTelefono] = useState('');
  const [qrUrl, setQrUrl] = useState<string | null>(null);
  const [subiendo, setSubiendo] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!visible) return;
    setNombre(petshop?.nombre ?? '');
    setCorto(petshop?.corto ?? '');
    setDueno(petshop?.dueno ?? '');
    setTelefono(petshop?.telefono ?? '');
    setQrUrl(petshop?.qrUrl ?? null);
    setError(null);
    setSaving(false);
    setSubiendo(false);
  }, [visible, petshop]);

  const onPickQr = () => {
    if (Platform.OS !== 'web') { setError('Subir foto solo disponible en web por ahora.'); return; }
    // @ts-ignore web-only
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      if (file.size > 10 * 1024 * 1024) { setError('Imagen muy pesada (máx 10 MB).'); return; }
      setSubiendo(true);
      setError(null);
      try {
        const r = await subirACloudinary(file, { folder: 'clinicat/petshops' });
        setQrUrl(r.url);
      } catch (e: any) {
        setError(e?.message ?? 'No se pudo subir la foto.');
      } finally {
        setSubiendo(false);
      }
    };
    input.click();
  };

  const onSave = async () => {
    if (!nombre.trim()) { setError('Poné el nombre del petshop.'); return; }
    setSaving(true);
    setError(null);
    try {
      const id = await upsertPetshop({
        id: petshop?.id,
        nombre: nombre.trim(),
        nombreCorto: corto.trim() || nombre.trim(),
        dueno: dueno.trim() || null,
        telefono: telefono.replace(/\D/g, '') || null,
        qrUrl,
      });
      onClose(petshop ? undefined : id);
    } catch (e: any) {
      setError(e?.message ?? 'No se pudo guardar.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet
      visible={visible}
      onClose={() => onClose()}
      size="sm"
      title={petshop ? `Editar ${petshop.corto}` : 'Nuevo petshop'}
      subtitle="Nombre, dueño y QR para rendir"
      footer={
        <>
          <Button label="Cancelar" variant="ghost" onPress={() => onClose()} disabled={saving} />
          <Button label={petshop ? 'Guardar' : 'Crear'} onPress={onSave} loading={saving} disabled={subiendo} />
        </>
      }
    >
      <TextField label="Nombre completo" placeholder="Ej: Petshop María" value={nombre} onChangeText={setNombre} />
      <TextField label="Nombre corto (el del botón)" placeholder="Ej: María" value={corto} onChangeText={setCorto} />
      <TextField label="Dueño" placeholder="Ej: María Pérez" value={dueno} onChangeText={setDueno} autoCapitalize="words" />
      <TextField label="Teléfono (para mandarle por WhatsApp)" placeholder="Ej: 70012345" value={telefono} onChangeText={setTelefono} keyboardType="phone-pad" hint="Solo el número, sin +591." />

      <View style={{ gap: 6 }}>
        <Text style={{ fontSize: 13, fontWeight: typography.weights.semibold, color: colors.ink[700] }}>Foto del QR (para transferirle)</Text>
        <Pressable
          onPress={onPickQr}
          style={({ hovered }) => ({
            height: 180,
            borderRadius: radius.xl,
            borderWidth: 1.5,
            borderStyle: 'dashed',
            borderColor: colors.magenta[300],
            backgroundColor: hovered ? colors.magenta[50] : colors.crema[50],
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            // @ts-ignore web only
            cursor: 'pointer',
          })}
        >
          {qrUrl ? (
            <Image source={{ uri: qrUrl }} style={{ width: '100%', height: '100%' }} /* @ts-ignore */ resizeMode="contain" />
          ) : (
            <View style={{ alignItems: 'center', gap: 6 }}>
              <QrCode size={28} color={colors.magenta[600]} />
              <Text style={{ fontSize: 13, color: colors.ink[600], fontWeight: typography.weights.semibold }}>{subiendo ? 'Subiendo…' : 'Tocá para subir el QR'}</Text>
            </View>
          )}
        </Pressable>
        {qrUrl ? (
          <Pressable onPress={onPickQr}>
            <Text style={{ fontSize: 12, color: colors.magenta[700], fontWeight: typography.weights.bold, textAlign: 'center' }}>Cambiar QR</Text>
          </Pressable>
        ) : null}
      </View>

      {error ? (
        <View style={{ padding: 12, borderRadius: 12, backgroundColor: colors.danger[50] }}>
          <Text style={{ color: colors.danger[600], fontSize: 13, fontWeight: typography.weights.semibold }}>{error}</Text>
        </View>
      ) : null}
    </Sheet>
  );
}
